import type {BlobItem} from "./blobs"

/** Compares dotted versions by their numbers, newest first. "26.2" sorts after "26.10". */
export function compareVersionsDescending(a: string, b: string): number {
  const numbers = (v: string) => (v.match(/\d+/g) ?? []).map(Number)
  const [x, y] = [numbers(a), numbers(b)]
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const diff = (y[i] ?? -1) - (x[i] ?? -1)
    if (diff !== 0) return diff
  }
  return b.localeCompare(a)
}

/** A release folder in `packages`, as build.yml names it: `<YYYY-MM-DD>_<tag>/`. */
export function parseReleaseFolder(prefix: string): {date: string; tag: string} | null {
  const match = /^(\d{4}-\d{2}-\d{2})_(.+)\/$/.exec(prefix)
  return match ? {date: match[1], tag: match[2]} : null
}

/** A zip in `releases`, named by hand: `<yymmdd>_<version>.zip` (one uses `-` instead of `_`). */
export function parseArchivedRelease(name: string): {date: string; version: string} | null {
  const match = /^(\d{2})(\d{2})(\d{2})[_-](.+)\.zip$/.exec(name)
  return match ? {date: `20${match[1]}-${match[2]}-${match[3]}`, version: match[4]} : null
}

export interface PluginFile {
  product: string
  version: string
  platform: string
  blob: BlobItem
}

/** `<product>/<version>/<platform>/<file>`, or `<product>/<version>/<file>` for a 64-bit-only plugin. */
export function parsePluginBlob(blob: BlobItem): PluginFile | null {
  const parts = blob.name.split("/")
  if (parts.length === 4) return {product: parts[0], version: parts[1], platform: parts[2], blob}
  if (parts.length === 3) return {product: parts[0], version: parts[1], platform: "x64", blob}
  return null
}

export interface TypelibFile {
  library: string
  asprovaVersion: string
  beta: boolean
  blob: BlobItem
}

/** `<library>/<version>/[<hash>/]<file>`, or the same under `beta/`. */
export function parseTypelibBlob(blob: BlobItem): TypelibFile | null {
  const beta = blob.name.startsWith("beta/")
  const parts = (beta ? blob.name.slice("beta/".length) : blob.name).split("/")
  if (parts.length < 3) return null
  return {library: parts[0], asprovaVersion: parts[1], beta, blob}
}

export function fileName(blobName: string): string {
  return blobName.slice(blobName.lastIndexOf("/") + 1)
}
